import React from 'react';

import { useDidMount, useTranslation } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import { PopupMenuItem, PopupMenuRef } from '@bcp-nextgen-dx-component-factory/design-system';
import { ActiveItem } from '@bcp-nextgen-dx-component-factory/design-system';
import { useBuildPhoneCodes } from '@hooks';
import { useNavigation } from '@navigation';

interface RequestToCallOpenHelperProps {
	indicative: string;
	setPhone: (phone: string) => void;
	setIndicative: (indicative: string) => void;
	setContactToCallCountry: (country: string) => void;
}

interface RequestToCallOpenResources {
	title: string;
	subtitle: string;
	inputLabel: string;
	inputCounty: string;
	linkBack: string;
	buttonCall: string;
	required: string;
	invalidPhone: string;
	indicativeGlobal: string;
}

interface RequestToCallOpenOutput {
	resources: RequestToCallOpenResources;
	buildDialingPhoneCodes: PopupMenuItem[];
	isDisable: boolean;
	popupMenuRef: React.RefObject<PopupMenuRef>;
	maxLengthContact?: number;
	onPressSelectCountry: (id?: ActiveItem | undefined) => void;
	onChangeValueIndicative: (value: string) => void;
	onClearValue: () => void;
	goTo: () => void;
}

export const useRequestToCallOpenHelper = (props: RequestToCallOpenHelperProps): RequestToCallOpenOutput => {
	const { indicative, setPhone, setIndicative, setContactToCallCountry } = props;
	const t = useTranslation();
	const { back } = useNavigation();
	const [isDisable, setIsDisable] = React.useState<boolean>(true);
	const { phoneCodes, initialPhoneId, getSelectedPhone } = useBuildPhoneCodes({ withFlags: false });
	const popupMenuRef = React.useRef<PopupMenuRef>(null);

	useDidMount(() => {
		popupMenuRef.current?.setActiveItem(0);
		if (initialPhoneId !== undefined) {
			popupMenuRef.current?.setActiveItem(initialPhoneId);
			const selectedPhone = getSelectedPhone(phoneCodes[initialPhoneId].content as string);
			setIndicative(`+${selectedPhone?.internationalCode as string}`);
			setContactToCallCountry(selectedPhone?.country as string);
		}
	});

	const onPressSelectCountry = (item: ActiveItem | undefined) => {
		if (item?.content !== undefined) {
			const selectedPhone = getSelectedPhone(item?.content);
			setIndicative(`+${selectedPhone?.internationalCode as string}`);
			setContactToCallCountry(selectedPhone?.country as string);
		}
		popupMenuRef.current?.hide();
	};

	const resources: RequestToCallOpenResources = {
		title: t('placeholders.requestToCall.open.title'),
		subtitle: t('placeholders.requestToCall.open.subtitle'),
		inputLabel: t('placeholders.requestToCall.form.phone'),
		inputCounty: t('placeholders.requestToCall.form.country'),
		linkBack: t('general.back'),
		buttonCall: t('placeholders.requestToCall.open.askToCall'),
		required: t('general.errors.required'),
		invalidPhone: t('general.errors.invalidPhone'),
		indicativeGlobal: `+${t('global.defaultInternationalDialing')}`,
	};

	const maxLengthContact = indicative === resources.indicativeGlobal ? 9 : undefined;

	const onChangeValue = (value: string, indicativeValue: string) => {
		if (
			value !== '' &&
			((indicativeValue === resources.indicativeGlobal && value.length === 9) ||
				indicativeValue !== resources.indicativeGlobal)
		) {
			setIsDisable(false);
			setPhone(value);
		} else {
			setIsDisable(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	};

	const onClearValue = React.useCallback(() => {
		setIsDisable(true);
		setPhone('');
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const goTo = React.useCallback(() => {
		back();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onChangeValueIndicative = (value: string) => {
		onChangeValue(value, indicative);
	};

	return {
		resources,
		isDisable,
		buildDialingPhoneCodes: phoneCodes,
		popupMenuRef,
		maxLengthContact,
		onPressSelectCountry,
		onChangeValueIndicative,
		onClearValue,
		goTo,
	};
};
